import React, { useEffect, useState, useRef } from 'react';
import { ReactComponent as ColumnSorterSvg } from '../../assets/icons/column-sorter.svg';
import { ReactComponent as ColumnSorterBlackSvg } from '../../assets/icons/column-sorter-black.svg';
import { Checkbox, ConfigProvider, Select, Typography } from 'antd';
import { useTheme } from '../../context/ThemeContext';
import Icon from '@ant-design/icons';
import { DARK } from '../../utilities/constants';
import PropTypes from 'prop-types';
import EmptyCard from '../EmptyCard/EmptyCard';

const { Text } = Typography;

const CustomMultiSelectDropdown = ({
  columns,
  onColumnsUpdate,
  disabled = false,
}) => {
  const initializedRef = useRef(false);
  const columnList = [
    { label: 'All', value: 'all' },
    ...columns.map(columnItem => ({
      label: columnItem?.title,
      value: columnItem?.key,
    })),
  ];
  const { theme } = useTheme();

  const allValues = columnList
    .filter(column => column.value !== 'all')
    .map(column => column.value);

  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (!initializedRef.current && columns?.length > 0) {
      const initialValues = columns.map(col => col.key);
      setSelectedItems(['all', ...initialValues]);
      initializedRef.current = true;
    }
  }, [columns]);

  const handleChange = values => {
    const hadAll = selectedItems.includes('all');
    const hasAll = values.includes('all');

    if (!hadAll && hasAll) {
      setSelectedItems(['all', ...allValues]);
    } else if (hadAll && !hasAll) {
      setSelectedItems([]);
    } else if (!hasAll && values.length === allValues.length) {
      setSelectedItems(['all', ...values]);
    } else if (hasAll && values.length < allValues.length + 1) {
      setSelectedItems(values.filter(value => value !== 'all'));
    } else {
      setSelectedItems(values);
    }
  };

  useEffect(() => {
    const updatedColumns = columns?.map(columnData =>
      selectedItems?.includes(columnData?.key)
        ? { ...columnData, hidden: false }
        : { ...columnData, hidden: true }
    );

    onColumnsUpdate(updatedColumns);
  }, [selectedItems]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Checkbox: {
            colorBgContainer: '#8A9097',
            colorBorder: '#8A9097',
            colorPrimary: 'var(--button-default-bg-color, #007BC0)',
            colorText: 'var(--primary-text-color, #FFFFFF)',
          },
          Select: {
            optionSelectedBg: 'var(--body-bg-color,#000000) !important',
            colorBgElevated: 'var(--body-bg-color,#000000) !important',
            colorText: 'var(--primary-text-color, #FFFFFF)',
          },
        },
      }}
    >
      <Select
        placeholder="Column Sorter"
        mode="multiple"
        options={columnList}
        style={{ width: 300 }}
        size="large"
        className="custom-select-dropdown multiple"
        maxTagCount={0} // Hide all tags
        showSearch={false}
        notFoundContent={<EmptyCard />}
        maxTagPlaceholder={() => (
          <Text style={{ fontSize: '1rem', lineHeight: 1.5 }}>
            Column Sorter
          </Text>
        )}
        menuItemSelectedIcon={null}
        optionRender={option => {
          // Handle the "All" option with intermediate state
          if (option?.value === 'all') {
            // Calculate if we're in an intermediate state (some but not all selected)
            const hasAllExceptAll =
              allValues.some(value => selectedItems.includes(value)) &&
              !allValues.every(value => selectedItems.includes(value));

            return (
              <Checkbox
                checked={selectedItems.includes('all')}
                indeterminate={
                  !selectedItems.includes('all') && hasAllExceptAll
                }
              >
                {option?.label}
              </Checkbox>
            );
          }

          // Regular options
          return (
            <Checkbox checked={selectedItems.includes(option?.value)}>
              {option?.label}
            </Checkbox>
          );
        }}
        onChange={handleChange}
        value={selectedItems}
        disabled={disabled}
        suffixIcon={
          <Icon
            component={theme === DARK ? ColumnSorterSvg : ColumnSorterBlackSvg}
            style={{
              pointerEvents: 'none',
            }}
          />
        }
        onSelect={value => {
          // When "all" is selected
          if (value === 'all') {
            setSelectedItems(['all', ...allValues]);
          }
        }}
        onDeselect={value => {
          // When "all" is deselected
          if (value === 'all') {
            setSelectedItems([]);
          }
        }}
      />
    </ConfigProvider>
  );
};

CustomMultiSelectDropdown.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  onColumnsUpdate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default CustomMultiSelectDropdown;
